/** @file Advances one track entry's clock and emits its start, keyed events and complete. */
import type { AnimEvent, TrackEntry } from '../contracts/state';
import type { AnimationData, SkeletonData } from '../skeletonData';
import { loopPass } from '../state/loopPass';
import { fireEventKeys } from './fireEventKeys';

/** Past every key: as the window end it closes a non-looping entry at `end` (W2-08 R6). */
const PAST_END = Number.POSITIVE_INFINITY;

/**
 * Non-looping: [a0, a1) before the end; [a0, past end) plus complete when the end is reached (once). "Reached" is
 * loopPass's rule (R10-01), the one stepState's take-over uses, so a clock a float error below the end completes in
 * the step a queued entry takes over, never after it.
 */
function fireOnce(data: SkeletonData, anim: AnimationData, track: number, entry: TrackEntry, a0: number, a1: number, out: AnimEvent[]): void {
  const end = anim.duration;
  if (loopPass(a0, end) >= 1) return;
  if (loopPass(a1, end) < 1) {
    fireEventKeys(data, anim, track, a0, a1, out);
    return;
  }
  fireEventKeys(data, anim, track, a0, PAST_END, out);
  out.push({ type: 'complete', track, anim: entry.anim });
}

/**
 * Keys and complete between lastTime and time in animation time; a loop fires at most one loop plus the remainder;
 * each pass of a loop closes like a non-looping end (a key at `end` fires before complete). Pass boundaries come
 * from loopPass, the rule addAnimation's take-over uses too.
 */
function fireRange(data: SkeletonData, track: number, entry: TrackEntry, fresh: boolean, out: AnimEvent[]): void {
  const anim = data.animations[entry.anim];
  if (anim === undefined) return;
  const a0 = entry.lastTime * entry.timeScale;
  const a1 = entry.time * entry.timeScale;
  const end = anim.duration;
  if (end <= 0) {
    if (!fresh) return;
    fireEventKeys(data, anim, track, 0, PAST_END, out);
    out.push({ type: 'complete', track, anim: entry.anim });
    return;
  }
  if (!entry.loop) {
    fireOnce(data, anim, track, entry, a0, a1, out);
    return;
  }
  const l0 = loopPass(a0, end);
  const l1 = loopPass(a1, end);
  if (l0 === l1) {
    fireEventKeys(data, anim, track, a0 - l0 * end, a1 - l1 * end, out);
    return;
  }
  fireEventKeys(data, anim, track, a0 - l0 * end, PAST_END, out);
  out.push({ type: 'complete', track, anim: entry.anim });
  fireEventKeys(data, anim, track, 0, a1 - l1 * end, out);
}

/**
 * Consumes entry.delay, then moves time forward by the rest of dt (lastTime = previous time). The delay counts as
 * used up under loopPass's slack (R10-04: delay 0.5 in 0.1 steps starts at 0.5, not one step late on a 3e-17
 * residue). On the first advance (lastTime < 0) emits interrupt(from) and start when `lifecycle`; when `keys`, emits
 * keyed events and complete.
 */
export function advanceEntry(data: SkeletonData, track: number, entry: TrackEntry, dt: number, lifecycle: boolean, keys: boolean, out: AnimEvent[]): void {
  let step = dt;
  if (entry.delay > 0) {
    if (loopPass(step, entry.delay) < 1) {
      entry.delay -= step;
      return;
    }
    step = Math.max(0, step - entry.delay);
    entry.delay = 0;
  }
  const fresh = entry.lastTime < 0;
  if (fresh && lifecycle) {
    if (entry.from !== null) out.push({ type: 'interrupt', track, anim: entry.from.anim });
    out.push({ type: 'start', track, anim: entry.anim });
  }
  entry.lastTime = entry.time;
  entry.time += step;
  if (keys) fireRange(data, track, entry, fresh, out);
}
